






import Enumerable from "linq";
import Moment from "moment";
import { Component, Mixins, Prop } from "vue-property-decorator";
import { EditingDocument } from "../editor";
import { DocumentInitializer, UserDefinitionLinkValueApplicator } from "../editor/helpers";
import { TimeMeasure } from "../../../utilities";
import { DomainRegistry } from "../../../domain/DomainRegistry";
import { IDocumentAlert } from "../../../domain/document/IDocumentAlert";
import {
  AbstractCheckableItem,
  AbstractOptionSelectableTextItem,
  AbstractValuedItem,
  CalculateItem,
  CheckBoxItem,
  CircleSelectItem,
  ComboBoxItem,
  DateItem,
  ExternalDataItem,
  ExternalProcessItem,
  IItem,
  MovableTextBoxItem,
  RollItem,
  TextBoxItem,
  TextItemOption,
  instanceOfIRelatedExternalScriptExecutable,
  IFreeInputable,
} from "../../../domain/document/items";
import {
  FuzzyDate,
} from "../../../domain/system/FuzzyDate";
import {
  IBehaviorDocumentEditorValidatorsConfiguration,
} from "../../../domain/configuration";
import {
  DocumentEditBehavior,
  RequestedObject,
  Document,
  NumericInputFormatter,
  UpdateKinds,
  DocumentRequestErrorCodes,
  INewDocumentRequest,
  instanceOfINewDocumentAlternativeRequest,
  IDocumentFactoryRequest,
  IDocumentFactoryRegistrationPostMessage,
  instanceOfIDocumentFactoryRegistrationPostMessage,
  DocumentParameterContainer,
} from "../../../domain/document";
import {
  TextLengthAlert
} from "../../../domain/document/alerts";
import {
  DateInputMethodKinds,
} from "../../../domain/document/inputMethodKinds";
import {
  DocumentAttributesValidator,
  EmptyValidator,
  NumericRangeValidator,
  CalculatedValueValidator,
  TextValidator,
} from "../../../domain/document/validators";

class DocumentRequestError extends Error {
  constructor(public readonly code: DocumentRequestErrorCodes, message?: string) {
    super(message);
  }
}

@Component
export default class RequestedDocumentProcessor extends Mixins() {
  @Prop()
  validator: IBehaviorDocumentEditorValidatorsConfiguration;

  @Prop({ default: false })
  shouldApplyValueForcibly: boolean;

  data(): any {
    return {
      requestedUrl: "",
      registrationResultMessage: null,
    };
  }
  requestedUrl: string;
  registrationResultMessage: IDocumentFactoryRegistrationPostMessage[];

  mounted() {
    window.addEventListener("message", this.onChildWindowNotify, false);
  }

  destroyed(): void {
    window.removeEventListener("message", this.onChildWindowNotify, false);
  }

  async onChildWindowNotify(event: any): Promise<void> {
    if (!event || (event && !event.data)) { return; }
    if (event.data.length === 0 || typeof event.data !== "string") { return; }
    const message = JSON.parse(event.data);
    this.registrationResultMessage = this.isDocumentFactoryRegistrationPostMessage(message)
      ? (message instanceof Array) ? message : [message]
      : null;
  }

  private isDocumentFactoryRegistrationPostMessage(message: any): boolean {
    return message instanceof Array
      ? message.some(m => instanceOfIDocumentFactoryRegistrationPostMessage(m))
      : instanceOfIDocumentFactoryRegistrationPostMessage(message);
  }

  public async process(requestId: number): Promise<void> {
    if (requestId == null) {
      throw new DocumentRequestError(DocumentRequestErrorCodes.InvalidRequest, "requestId is empty!!!");
    }
    this.$logger.debug("requestId: " + requestId);

    const repository = DomainRegistry.document.getDocumentRepository();
    try {
      const request = await this.getNewDocumentRequest(requestId);
      if (!request) {
        throw new DocumentRequestError(DocumentRequestErrorCodes.InvalidRequest, "request is empty!!!");
      }
      if (instanceOfINewDocumentAlternativeRequest(request)) {
        await this.registerDocumentByRequestedUrl(requestId, request.url);
        return;
      }
      const factoryRequest = request as IDocumentFactoryRequest;
      if (!factoryRequest.patientNo) {
        throw new DocumentRequestError(DocumentRequestErrorCodes.InvalidRequest, "patientNo is empty!!!");
      }
      if (factoryRequest.formatOriginalId == null) {
        throw new DocumentRequestError(DocumentRequestErrorCodes.InvalidRequest, "formatOriginalId is empty!!!");
      }

      const document = await repository.getNewDocument(factoryRequest.patientNo, factoryRequest.formatOriginalId, factoryRequest.institutionNo, factoryRequest.regionalInstitutionNo, factoryRequest.hisInfoGetterParameter, factoryRequest.systemKey, factoryRequest.linkKey);
      await this.processDocument(document, factoryRequest.objects, factoryRequest.systemKey, factoryRequest.linkKey, factoryRequest.institutionNo, factoryRequest.regionalInstitutionNo, factoryRequest.hisInfoGetterParameter, null);
      
      // 一時保存時は警告チェックしない
      // TODO: 未入力警告設定対応するべきかどうか
      document.attributes.isTemporary = !!factoryRequest.isTemporary;
      if (!factoryRequest.isTemporary && !this.validateDocument(document)) {
        throw new DocumentRequestError(DocumentRequestErrorCodes.ValidationError, "登録時検証でエラーが検出されたため登録できません。");
      }

      await TimeMeasure.logAsync(
        "要求文書登録",
        async () => {
          await repository.registerRequestedNewDocument(requestId, document);
        },
        "",
      );
    } catch (e) {
      this.$logger.error(e);
      await TimeMeasure.logAsync(
        "失敗結果登録",
        async () => {
          // Errorがinterfaceだからかextendsしてもinstanceofで型判定できない.
          // 諦めてプロパティの有無で判定する.
          await repository.registerFailureInformationToNewDocumentRequest(
            requestId,
            e.code ? e.code : DocumentRequestErrorCodes.UncaughtException,
            e.message ? e.message : "予期せぬ例外が発生しました。");
        },
        "",
      );
      throw e;
    }
  }

  /**
   * 文書を初期化したのち、指定された値で上書きする
   */
  private async processDocument(document: Document, objects: RequestedObject[], systemKey: string, linkKey: string, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string, parameterContainer: DocumentParameterContainer): Promise<void> {
    // 文書の初期化（HIS項目の適用など）
    // TODO: 作成日・最終更新日などはどうするのがよい？
    // 初期化前の事前処理
    this.updateDocument(document);
    const initializer = new DocumentInitializer(document);
    const behavior = new DocumentEditBehavior();
    behavior.isCommitPhase = true;
    behavior.shouldPrintSpecifiedSectionAtFirstRegistration = false;
    await initializer.initializeNewDocument(null, systemKey, linkKey, institutionNo, regionalInstitutionNo, hisInfoGetterParameter, parameterContainer, behavior, []);
    const editingDocument = new EditingDocument(document);
    await editingDocument.initialize();

    // 指定された値を上書き
    await TimeMeasure.log(
      "指定値反映",
      async () => this.applyRequestedValues(editingDocument, objects),
      "",
    );

    await TimeMeasure.logAsync(
      "ユーザ定義のリンク値の補正",
      async () => {
        await this.applyUserDefinitionLinkValue(editingDocument);
      },
      "",
    );
  }

  private updateDocument(document: Document): void {
    const itemSetId = 0;
    for (const item of document.itemSets[itemSetId].items) {
      if (!item) {
        continue;
      }
      this.updateExternalProcessItemToNoneUpdateKind(item);
      this.updateExternalDataItemToNoneUpdateKind(item);
      this.removeScriptExecuteParameters(item);
    }
  }

  /**
   * 外部処理をスキップさせるためupdateTypeを更新しないに変更しておく
   */
  private updateExternalProcessItemToNoneUpdateKind(item: IItem): void {
    if (item instanceof ExternalProcessItem) {
      item.updateKind = UpdateKinds.None;
    }
  }

  /**
   * 外部データをスキップさせるためupdateTypeを更新しないに変更しておく
   */
  private updateExternalDataItemToNoneUpdateKind(item: IItem): void {
    if (item instanceof ExternalDataItem) {
      item.updateKind = UpdateKinds.None;
    }
  }

  /**
   * リレーショナル契機で外部処理を行わないように削除する
   */
  private removeScriptExecuteParameters(item: IItem): void {
    if (instanceOfIRelatedExternalScriptExecutable(item)) {
      item.scriptExecuteParameters = [];
    }
  }

  public async processExistingRequestedDocument(requestId: number, documentId: number): Promise<void> {
    if (requestId == null) {
      throw new DocumentRequestError(DocumentRequestErrorCodes.InvalidRequest, "requestId is empty!!!");
    }
    if (documentId == null) {
      throw new DocumentRequestError(DocumentRequestErrorCodes.InvalidRequest, "documentId is empty!!!");
    }

    this.$logger.debug("requestId: " + requestId + ", documentId: " + documentId);

    const repository = DomainRegistry.document.getDocumentRepository();
    try {
      const request = await this.getNewDocumentRequest(requestId);
      if (!request) {
        throw new DocumentRequestError(DocumentRequestErrorCodes.InvalidRequest, "request is empty!!!");
      }
      const factoryRequest = request as IDocumentFactoryRequest;
      if (!factoryRequest.patientNo) {
        throw new DocumentRequestError(DocumentRequestErrorCodes.InvalidRequest, "patientNo is empty!!!");
      }

      const document = await repository.getDocument(factoryRequest.patientNo, documentId, null, null, null);
      await this.processExistingDocument(document, factoryRequest.objects);
      
      // 一時保存時は警告チェックしない
      document.attributes.isTemporary = !!factoryRequest.isTemporary;
      if (!factoryRequest.isTemporary && !this.validateDocument(document)) {
        throw new DocumentRequestError(DocumentRequestErrorCodes.ValidationError, "登録時検証でエラーが検出されたため登録できません。");
      }

      await TimeMeasure.logAsync(
        "要求文書登録",
        async () => {
          await repository.registerRequestedNewDocument(requestId, document);
        },
        "",
      );
    } catch (e) {
      this.$logger.error(e);
      await TimeMeasure.logAsync(
        "失敗結果登録",
        async () => {
          // Errorがinterfaceだからかextendsしてもinstanceofで型判定できない.
          // 諦めてプロパティの有無で判定する.
          await repository.registerFailureInformationToNewDocumentRequest(
            requestId,
            e.code ? e.code : DocumentRequestErrorCodes.UncaughtException,
            e.message ? e.message : "予期せぬ例外が発生しました。");
        },
        "",
      );
      throw e;
    }
  }

  private async getNewDocumentRequest(requestId: number): Promise<INewDocumentRequest> {
    try {
      const repository = DomainRegistry.document.getDocumentRepository();
      return await repository.getNewDocumentRequest(requestId);
    } catch (e) {
      this.$logger.error(e);
      return null;
    }
  }

  /**
   * 文書を初期化したのち、指定された値で上書きする
   */
  private async processExistingDocument(document: Document, objects: RequestedObject[]): Promise<void> {
    // 文書の初期化（HIS項目の適用など）
    // 初期化前の事前処理
    this.updateDocument(document);
    const initializer = new DocumentInitializer(document);
    const behavior = new DocumentEditBehavior();
    behavior.isCommitPhase = true;
    behavior.shouldPrintSpecifiedSectionAtFirstRegistration = false;
    await initializer.initializeExistingDocument(null, null, behavior, []);

    const editingDocument = new EditingDocument(document);
    await editingDocument.initialize();

    // 指定された値を上書き
    await TimeMeasure.log(
      "指定値反映",
      async () => this.applyRequestedValues(editingDocument, objects),
      "",
    );

    await TimeMeasure.logAsync(
      "ユーザ定義のリンク値の補正",
      async () => {
        await this.applyUserDefinitionLinkValue(editingDocument);
      },
      "",
    );
  }

  public async applyRequestedValues(document: EditingDocument, requestObjects: RequestedObject[]): Promise<void> {
    this.updateCheckableItemToMultiSelect(document, requestObjects);
    for (const requestObject of requestObjects) {
      this.applyRequestedValue(document, requestObject);
    }
    const isScriptSucceeded = await document.executeStackedScriptsAsyncWithResult();
    if (!isScriptSucceeded) {
      this.$error("外部スクリプト", "外部スクリプトの実行中にエラーが発生しています。");
    }
  }

  /**
   * 単選択のチェック・円選択のオブジェクトに対して複数選択でないと対応できない一連の値が指定されているとき、
   * 複数選択可に変更しておく
   */
  private updateCheckableItemToMultiSelect(document: EditingDocument, requestObjects: RequestedObject[]): void {
    const itemSetId = 0;
    const targetItems: AbstractCheckableItem[] = [];
    for (const item of document.itemSets[itemSetId].items) {
      if (!item) { continue; }
      if (item instanceof AbstractCheckableItem && !item.isMultiCheckable) {
        if (Enumerable.from(requestObjects).any(r => this.isRequestMatched(item, r) && r.value === item.itemName)) {
          // チェック・円選択を入れる単選択のオブジェクト
          targetItems.push(item);
        }
      }
    }

    const objectNames = Enumerable.from(targetItems)
      .where(i => !!i.name)
      .select(i => i.name)
      .distinct()
      .toArray();
    
    for (const objectName of objectNames) {
      // TODO: 連動範囲（ページ内・全体）の考慮は未対応
      const nameFilteredItems = targetItems.filter(i => i.name === objectName);
      if (nameFilteredItems.length < 2) {
        // ひとつしか選択されないので複数選択にする必要は無いはず
        continue;
      }
      for (const nameFilteredItem of nameFilteredItems) {
        const item = document.itemSets[itemSetId].items[nameFilteredItem.privateId];
        const cloned = item.clone() as AbstractCheckableItem;
        cloned.isMultiCheckable = true;
        document.updateItem(cloned, {
          itemSetId,
        });
      }
    }
  }

  private applyRequestedValue(document: EditingDocument, requestObject: RequestedObject): void {
    // 続紙は無いはず
    const itemSetId = 0;
    for (const originalItem of document.itemSets[itemSetId].items) {
      if (!originalItem) { continue; }

      // TODO: ユーザ定義の採番記号の対応
      if (!this.isRequestMatched(originalItem, requestObject)) { continue; }
      const item = document.itemSets[itemSetId].items[originalItem.privateId];
      const cloned = item.clone();
      this.applyRequestedValueToItem(cloned, requestObject);
      document.updateItem(cloned, {
        itemSetId,
      });
    }
  }

  private isRequestMatched(item: IItem, requestObject: RequestedObject): boolean {
    if (!requestObject.id && !requestObject.key) {
      this.$logger.warn("idもkeyも指定されていない指示があります。");
      return false;
    }
    return (requestObject.id && requestObject.key)
      ? this.isIdMatched(item, requestObject) && this.isKeyMatched(item, requestObject)
      : this.isIdMatched(item, requestObject) || this.isKeyMatched(item, requestObject);
  }

  private isIdMatched(item: IItem, requestObject: RequestedObject): boolean {
    return requestObject.id && requestObject.id === item.id;
  }

  private isKeyMatched(item: IItem, requestObject: RequestedObject): boolean {
    return requestObject.key && item instanceof AbstractValuedItem && requestObject.key === item.name;
  }

  private applyRequestedValueToItem(item: IItem, requestObject: RequestedObject) {
    // TODO: refactor
    const value = requestObject.value ? requestObject.value : "";
    if (item instanceof TextBoxItem) {
      this.applyRequestedValueToTextBoxItem(item as TextBoxItem, value);
      return;
    }
    if (item instanceof DateItem) {
      this.applyRequestedValueToDateItem(item as DateItem, value);
      return;
    }
    if (item instanceof CheckBoxItem) {
      this.applyRequestedValueToCheckableItem(item as CheckBoxItem, value);
      return;
    }
    if (item instanceof CircleSelectItem) {
      this.applyRequestedValueToCheckableItem(item as CircleSelectItem, value);
      return;
    }
    if (item instanceof ComboBoxItem) {
      this.applyRequestedValueToOptionSelectableTextItem(item as ComboBoxItem, value);
      return;
    }
    if (item instanceof RollItem) {
      this.applyRequestedValueToOptionSelectableTextItem(item as RollItem, value);
      return;
    }
    if (item instanceof ExternalDataItem) {
      this.applyRequestedValueToExternalDataItem(item as ExternalDataItem, value);
      return;
    }
    if (item instanceof CalculateItem) {
      this.applyRequestedValueToCalculateItem(item as CalculateItem, value);
      return;
    }
    if (item instanceof MovableTextBoxItem) {
      this.applyRequestedValueToMovableTextBoxItem(item as MovableTextBoxItem, value);
      return;
    }
    this.$logger.info("unhandled requested value. id=" + requestObject.id + ", key=" + requestObject.key);
  }

  private applyRequestedValueToFreeInputtableItem<T extends IItem & IFreeInputable>(item: T, apply: (i: T) => void, shouldApplyValueForcibly: boolean): void {
    if (!shouldApplyValueForcibly) {
      if (!item.isFreeInputable) {
        this.$logger.warn("フリー入力に変更できませんでした。id=" + item.id);
        return;
      }
      item.shouldInputFree = true;
    }
    apply(item);
  }

  private applyRequestedValueToTextBoxItem(item: TextBoxItem, value: string): void {
    this.applyRequestedValueToFreeInputtableItem(item, i => {
      const insertText = i.isNumeric
        ? NumericInputFormatter.format(value, i.numericFormat)
        : value;
      i.value = insertText;
      i.adjustObjectHeight();
      i.adjustFontSize();
    }, this.shouldApplyValueForcibly);
  }

  private applyRequestedValueToDateItem(item: DateItem, value: string): void {
    const regex = item.value.isTime ? /^(\d{6}|\d{14})$/ : /^(\d{8}|\d{14})$/;
    const format = value.length === 6 ? "HHmmss" : value.length === 8 ? "YYYYMMDD" : "YYYYMMDDHHmmss";
    // クリアにも対応する.(00000000という文字列を入れられなくなるがそれは20211230とかが入れられないのと一緒なので.)
    const dateTime = this.isDateItemValueCleared(value, item.value.isTime)
      ? null
      : Moment(value, format).toDate();
    const fuzzyDate = item.value as FuzzyDate;
    if (!value.match(regex) || ((dateTime !== null) && (dateTime.toString() === "Invalid Date"))) {
      this.applyRequestedValueToFreeInputtableItem(item, i => i.value = new FuzzyDate(value, fuzzyDate.isTime), this.shouldApplyValueForcibly);
    } else {
      if (item.shouldCalculate) {
        item.inputMethodKind = DateInputMethodKinds.Calendar;
      }
      item.value = new FuzzyDate(dateTime, fuzzyDate.isTime);
    }
    item.adjustFontSize();
  }

  private isDateItemValueCleared(value: string, isTime: boolean): boolean {
    return (isTime && (value === "00000000000000"))
        || (!isTime && (value === "00000000"));
  }

  private applyRequestedValueToCheckableItem(item: AbstractCheckableItem, value: string): void {
    if (item.itemName === value) {
      item.isChecked = true;
    } else if (value === "") {
      item.isChecked = false;
    }
  }

  private applyRequestedValueToOptionSelectableTextItem(item: AbstractOptionSelectableTextItem, value: string): void {
    const selectTarget = this.getTextItemOption(item.options, value);
    if (!selectTarget) {
      if (!value && !item.label) { return; }
      this.applyRequestedValueToFreeInputtableItem(item, i => {
        i.label = value;
        i.value = value;
        i.score = null;
        i.code = null;
      }, false);
      return;
    }

    for (const menuItem of item.options) {
      menuItem.isSelected = false;
    }
    selectTarget.isSelected = true;
    item.applyOptionValue(selectTarget);
  }

  private getTextItemOption(menuItems: TextItemOption[], text: string): TextItemOption {
    for (const menuItem of menuItems) {
      if (menuItem.text === text) {
        return menuItem;
      }
    }
    return null;
  }

  private applyRequestedValueToExternalDataItem(item: ExternalDataItem, value: string): void {
    this.applyRequestedValueToFreeInputtableItem(item, i => {
      i.value = value;
      i.adjustFontSize();
    }, this.shouldApplyValueForcibly);
  }

  private applyRequestedValueToCalculateItem(item: CalculateItem, value: string): void {
    this.applyRequestedValueToFreeInputtableItem(item, i => {
      i.value = value;
      i.adjustFontSize();
    }, false);
  }

  private applyRequestedValueToMovableTextBoxItem(item: MovableTextBoxItem, value: string): void {
    this.applyRequestedValueToFreeInputtableItem(item, i => {
      i.value = value;
      i.adjustFontSize();
    }, this.shouldApplyValueForcibly);
  }

  private validateDocument(document: Document): boolean {
    const alerts = this.findAlerts(document);
    alerts.forEach(a => {
      if (this.validator.textLength.isError && a instanceof TextLengthAlert) {
        a.isError = true;
      }
      if (a.isError) {
        this.$logger.warn(a.description + " id=" + a.detectedItem.id);
      }
    });
    return Enumerable.from(alerts).all(a => !a.isError);
  }

  private findAlerts(document: Document): IDocumentAlert[] {
    return Enumerable.from([
      new DocumentAttributesValidator(),
      new EmptyValidator(),
      new NumericRangeValidator(),
      new CalculatedValueValidator(),
      new TextValidator(),
    ])
      .where(v => !!v)
      .select(v => v.validate(document))
      .aggregate([] as IDocumentAlert[], (s, a) => s.concat(a));
  }

  private async applyUserDefinitionLinkValue(editingDocument: EditingDocument): Promise<void> {
    const applicator = new UserDefinitionLinkValueApplicator(editingDocument);
    await applicator.apply();
  }

  private async registerDocumentByRequestedUrl(requestId: number, url: string) {
    this.requestedUrl = url;
    await this.$wait(() => !!this.registrationResultMessage);
    for (const message of this.registrationResultMessage) {
      if (message.isOk) {
        await this.registerSuccessInformationToNewDocumentRequest(requestId, message.documentId, message.archiveId);
      } else {
        throw new DocumentRequestError(
          DocumentRequestErrorCodes.InvalidRequest,
          this.registrationResultMessage ? message.errorMessage : "指定したURLでの文書登録に失敗しました。"
        );
      }
    }
  }

  private async registerSuccessInformationToNewDocumentRequest(requestId: number, documentId: number, archiveId: number): Promise<void> {
    try {
      const repository = DomainRegistry.document.getDocumentRepository();
      await repository.registerSuccessInformationToNewDocumentRequest(requestId, documentId, archiveId);
    } catch (e) {
      throw new DocumentRequestError(DocumentRequestErrorCodes.InvalidRequest, e.message);
    }
  }
}
